import { ref } from 'vue';
import { get } from '@utils/api';
import { useEnvProps } from '@composables/useEnvProps';
import useFetch from '../api/useFetch';
/**
 * @param {string} word
 * @param {number} count
 * @returns {string}
 */
const pluralize = (word, count) => (count === 1 ? word : `${word}s`);

export const useReviewerProfile = () => {
  const REVIEWS_PER_PAGE = 8;
  const page = ref(1);
  const { hostlink } = useEnvProps();

  /**
   * @param {ProductId} productId
   * @returns {Promise<Product>}
  */
  const fetchProduct = async (productId) => {
    const rawResult = await get(`${hostlink}/xapi/digital/v1/product/${productId}?clientId=REVIEW`);
    return rawResult.product ? rawResult.product[0] : { id: productId };
  };

  /**
  * @param {ProductId[]} productIds
  */
  const fetchProducts = async (productIds) => Promise.allSettled(productIds.map(fetchProduct));
  /**
  * @param { {id: ProductId, newPage: number} } param
  */
  const getReviewerProfile = async ({ id, newPage = 1 }) => {
    page.value = newPage;
    const url = `${hostlink}/xapi/digital/v1/product/reviews/author/${id}?limitReviews=${REVIEWS_PER_PAGE}&offsetReviews=${
      (newPage - 1) * REVIEWS_PER_PAGE
    }`;
    const response = await get(url);
    if (error.value) throw new Error(`Failed to fetch reviewer data${error.value}`); // eslint-disable-line no-use-before-define
    const data = response;
    if (!data.authors) throw new Error('NO reviewer data');
    const reviewsStatsData = data.authors[0].reviewStatistics;
    const authorReviewsData = data.authors[0].reviews;
    const totalAuthorReviewsText = `${reviewsStatsData.totalReviewCount} ${pluralize('Review', reviewsStatsData.totalReviewCount)}`;
    const totalHelpfulVotesText = `${reviewsStatsData.helpfulVoteCount} ${pluralize('Vote', reviewsStatsData.helpfulVoteCount)}`;
    const productIds = authorReviewsData.map((/** @type {Review} */ review) => review.productId);
    const products = await fetchProducts(productIds);

    const enrichedReviews = products.map((product, index) => {
      const productName = product?.value?.detail?.name;
      const productUrl = product?.value?.identifier?.productUrl;
      return {
        ...authorReviewsData[index],
        productData: {
          id: product?.value?.id,
          available: !!product?.value?.availability?.available,
          name: productName,
          url: productUrl ? `${hostlink}${productUrl}` : '',
          imgUrl: product?.value?.urlTemplate?.product + product?.value?.imagery?.images[0].filePath,
          productBrandAndName: [product?.value?.detail?.brand?.name, productName].filter(Boolean).join(' '),
        },
      };
    });

    return {
      totalAuthorReviews: reviewsStatsData.totalReviewCount,
      authorReviews: enrichedReviews,
      totalAuthorReviewsText,
      totalHelpfulVotesText,
    };
  };

  const {
    result, isLoading, error, fetch,
  } = useFetch(getReviewerProfile, { initialValue: null });

  return {
    reviewerProfileData: result,
    isLoading,
    error,
    fetchProfileData: fetch,
    page,
  };
};

export default useReviewerProfile;
